/**
 * L6 — plateforme -> tenant : bandeau d'annonces globales.
 *
 * Affiche au plus une annonce à la fois (la plus récente encore active).
 * info/warning peuvent être fermées par l'utilisateur ; critical reste
 * toujours affichée.
 */

import { useAnnouncements } from '@/hooks/useSubscription'
import type { Announcement } from '@/api/announcements'
import { AlertCircle, AlertTriangle, Info, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

function dismissedKey(id: string): string {
  return `announcement-dismissed-${id}`
}

function isDismissed(id: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(dismissedKey(id)) === '1'
  } catch {
    return false
  }
}

function setDismissed(id: string): void {
  try {
    window.localStorage.setItem(dismissedKey(id), '1')
  } catch {
    /* localStorage may be blocked; ignore */
  }
}

function pickBanner(list: Announcement[] | undefined): Announcement | null {
  if (!list || list.length === 0) return null
  const now = Date.now()
  const active = list.filter((a) => {
    const starts = new Date(a.starts_at).getTime()
    if (starts > now) return false
    if (a.ends_at) {
      const ends = new Date(a.ends_at).getTime()
      if (ends < now) return false
    }
    return true
  })
  if (active.length === 0) return null
  // Newest starts_at wins.
  active.sort((a, b) => new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime())
  return active[0] ?? null
}

const SEVERITY_STYLES = {
  info: {
    icon: Info,
    className:
      'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900/60 dark:bg-blue-950/70 dark:text-blue-100',
  },
  warning: {
    icon: AlertTriangle,
    className:
      'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/70 dark:text-amber-100',
  },
  critical: {
    icon: AlertCircle,
    className:
      'border-red-200 bg-red-50 text-red-900 dark:border-red-900/60 dark:bg-red-950/70 dark:text-red-100',
  },
} as const

export function AnnouncementsBanner() {
  const { data } = useAnnouncements()
  const current = useMemo(() => pickBanner(data), [data])
  const [dismissedId, setDismissedIdState] = useState<string | null>(null)

  useEffect(() => {
    if (current && isDismissed(current.id)) setDismissedIdState(current.id)
    else setDismissedIdState(null)
  }, [current])

  if (!current) return null
  if (dismissedId === current.id && current.severity !== 'critical') return null

  const style = SEVERITY_STYLES[current.severity]
  const Icon = style.icon
  const canDismiss = current.severity !== 'critical'

  const onDismiss = () => {
    if (!canDismiss) return
    setDismissed(current.id)
    setDismissedIdState(current.id)
  }

  return (
    <div
      role="status"
      className={`w-full border-b px-4 py-2 md:px-6 lg:px-8 ${style.className}`}
    >
      <div className="mx-auto flex w-full max-w-7xl items-start gap-3">
        <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="flex-1">
          <p className="text-sm font-semibold">{current.title}</p>
          {current.body ? (
            <p className="text-xs opacity-90 whitespace-pre-line">{current.body}</p>
          ) : null}
        </div>
        {canDismiss ? (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Fermer"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-black/5 dark:hover:bg-white/10"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  )
}
